import React, { useEffect, useState } from 'react';
import { adminFetch } from '../../lib/api';
import { Users, Plus, X, Edit, Globe, Mail, Phone, Building2, Link, Percent } from 'lucide-react';

interface Partner {
  id: string;
  name: string;
  type: string;
  contact_person: string;
  contact_email: string;
  contact_phone: string;
  website: string;
  status: string;
  referral_code: string;
  copecart_affiliate_id: string;
  commission_model: string;
  commission_rate: number;
  notes: string;
  referral_count?: number;
  created_at: string;
}

const TYPE_MAP: Record<string, { label: string; emoji: string }> = {
  verband: { label: 'Verband', emoji: '🏛️' },
  influencer: { label: 'Influencer', emoji: '📱' },
  berater: { label: 'Berater', emoji: '🎯' },
  fahrlehrer: { label: 'Fahrlehrer', emoji: '🚗' },
  affiliate: { label: 'Affiliate', emoji: '🔗' },
  kooperation: { label: 'Kooperation', emoji: '🤝' },
  sonstige: { label: 'Sonstige', emoji: '📋' },
};

const STATUS_MAP: Record<string, { label: string; color: string }> = {
  active: { label: 'Aktiv', color: '#10b981' },
  inactive: { label: 'Inaktiv', color: '#6b7280' },
  prospect: { label: 'Prospect', color: '#f59e0b' },
};

const EMPTY_PARTNER = {
  name: '', type: 'sonstige', contact_person: '', contact_email: '', contact_phone: '',
  website: '', status: 'prospect', referral_code: '', copecart_affiliate_id: '',
  commission_model: '', commission_rate: 0, notes: '',
};

export const SalesPartnersPage: React.FC = () => {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Partner | null>(null);
  const [form, setForm] = useState({ ...EMPTY_PARTNER });
  const [saving, setSaving] = useState(false);
  const [referrals, setReferrals] = useState<any[]>([]);
  const [showReferrals, setShowReferrals] = useState<string | null>(null);

  useEffect(() => { loadPartners(); }, []);

  const loadPartners = async () => {
    try {
      const res = await adminFetch('/api/admin/sales/partners');
      const data = await res.json();
      setPartners(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
      setPartners([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const method = editing ? 'PUT' : 'POST';
      const url = editing ? `/api/admin/sales/partners/${editing.id}` : '/api/admin/sales/partners';
      await adminFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      setShowForm(false);
      setEditing(null);
      setForm({ ...EMPTY_PARTNER });
      loadPartners();
    } catch (error) {
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Partner deaktivieren?')) return;
    await adminFetch(`/api/admin/sales/partners/${id}`, { method: 'DELETE' });
    loadPartners();
  };

  const openReferrals = async (partnerId: string) => {
    setShowReferrals(partnerId);
    try {
      const res = await adminFetch(`/api/admin/sales/partners/${partnerId}/referrals`);
      setReferrals(await res.json());
    } catch { setReferrals([]); }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1><Users style={{ marginRight: 8, verticalAlign: 'middle' }} size={24} /> Partner & Multiplikatoren</h1>
        <button className="btn btn-primary" onClick={() => { setForm({ ...EMPTY_PARTNER }); setEditing(null); setShowForm(true); }}>
          <Plus size={14} /> Neuer Partner
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => <div key={i} className="card skeleton" style={{ height: 180 }} />)
        ) : partners.length === 0 ? (
          <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)', gridColumn: '1/-1' }}>
            Keine Partner vorhanden
          </div>
        ) : (
          partners.map(p => (
            <div key={p.id} className="card" style={{ padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 20 }}>{TYPE_MAP[p.type]?.emoji || '📋'}</span>
                    <h3 style={{ margin: 0, fontSize: 16 }}>{p.name}</h3>
                  </div>
                  <span style={{
                    display: 'inline-block', marginTop: 6, fontSize: 11, padding: '2px 8px',
                    borderRadius: 8, fontWeight: 600, color: '#fff',
                    background: STATUS_MAP[p.status]?.color || '#666',
                  }}>
                    {STATUS_MAP[p.status]?.label || p.status}
                  </span>
                  <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--text-secondary)' }}>
                    {TYPE_MAP[p.type]?.label || p.type}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => {
                    setEditing(p);
                    setForm({
                      name: p.name, type: p.type, contact_person: p.contact_person || '', contact_email: p.contact_email || '',
                      contact_phone: p.contact_phone || '', website: p.website || '', status: p.status,
                      referral_code: p.referral_code || '', copecart_affiliate_id: p.copecart_affiliate_id || '',
                      commission_model: p.commission_model || '', commission_rate: p.commission_rate || 0, notes: p.notes || '',
                    });
                    setShowForm(true);
                  }}><Edit size={14} /></button>
                </div>
              </div>

              <div style={{ fontSize: 13, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {p.contact_person && <div>👤 {p.contact_person}</div>}
                {p.contact_email && <div><Mail size={12} style={{ marginRight: 4 }} />{p.contact_email}</div>}
                {p.referral_code && <div><Link size={12} style={{ marginRight: 4 }} />Code: <code>{p.referral_code}</code></div>}
                {p.commission_model && (
                  <div><Percent size={12} style={{ marginRight: 4 }} />{p.commission_model}: {p.commission_rate}%</div>
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  {p.referral_count || 0} Referrals
                </span>
                <button className="btn btn-ghost btn-sm" onClick={() => openReferrals(p.id)} style={{ fontSize: 12 }}>
                  Referrals ansehen →
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Partner Form Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className="modal-header">
              <h2>{editing ? 'Partner bearbeiten' : 'Neuer Partner'}</h2>
              <button className="btn btn-ghost" onClick={() => setShowForm(false)}><X size={16} /></button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, padding: 20 }}>
              <div style={{ gridColumn: '1/-1' }}>
                <label>Name *</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <label>Typ</label>
                <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                  {Object.entries(TYPE_MAP).map(([k, v]) => <option key={k} value={k}>{v.emoji} {v.label}</option>)}
                </select>
              </div>
              <div>
                <label>Status</label>
                <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                  {Object.entries(STATUS_MAP).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              <div><label>Ansprechpartner</label><input value={form.contact_person} onChange={e => setForm({ ...form, contact_person: e.target.value })} /></div>
              <div><label>E-Mail</label><input value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })} /></div>
              <div><label>Telefon</label><input value={form.contact_phone} onChange={e => setForm({ ...form, contact_phone: e.target.value })} /></div>
              <div><label>Website</label><input value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} placeholder="https://" /></div>
              <div><label>Referral-Code</label><input value={form.referral_code} onChange={e => setForm({ ...form, referral_code: e.target.value })} placeholder="PARTNER123" /></div>
              <div><label>CopeCart Affiliate-ID</label><input value={form.copecart_affiliate_id} onChange={e => setForm({ ...form, copecart_affiliate_id: e.target.value })} /></div>
              <div>
                <label>Provisionsmodell</label>
                <select value={form.commission_model} onChange={e => setForm({ ...form, commission_model: e.target.value })}>
                  <option value="">Kein</option>
                  <option value="fixed">Festbetrag</option>
                  <option value="percentage">Prozentual</option>
                  <option value="tiered">Gestaffelt</option>
                </select>
              </div>
              <div><label>Provisionssatz (%)</label><input type="number" value={form.commission_rate} onChange={e => setForm({ ...form, commission_rate: parseInt(e.target.value) || 0 })} /></div>
              <div style={{ gridColumn: '1/-1' }}>
                <label>Notizen</label>
                <textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 20px', borderTop: '1px solid var(--border)' }}>
              <button className="btn btn-ghost" onClick={() => setShowForm(false)}>Abbrechen</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving || !form.name}>
                {saving ? 'Speichern...' : editing ? 'Aktualisieren' : 'Erstellen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Referrals Modal */}
      {showReferrals && (
        <div className="modal-overlay" onClick={() => setShowReferrals(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 500 }}>
            <div className="modal-header">
              <h2>Referrals</h2>
              <button className="btn btn-ghost" onClick={() => setShowReferrals(null)}><X size={16} /></button>
            </div>
            <div style={{ padding: 20 }}>
              {referrals.length === 0 ? (
                <p style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>Keine Referrals</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {referrals.map((r: any) => (
                    <div key={r.id} style={{
                      padding: '10px 14px', background: 'var(--bg-secondary)', borderRadius: 8,
                      display: 'flex', justifyContent: 'space-between', fontSize: 13,
                    }}>
                      <div>
                        <strong>{r.sales_leads?.name || 'Unbekannt'}</strong>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{r.sales_leads?.city}</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <span style={{
                          padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 600,
                          background: r.status === 'converted' ? '#10b98115' : '#f59e0b15',
                          color: r.status === 'converted' ? '#10b981' : '#f59e0b',
                        }}>{r.status}</span>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
                          {new Date(r.created_at).toLocaleDateString('de-DE')}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
